import { useId } from 'react';
import type { ReactElement } from 'react';

const STUB_FILL = '#F0B429';
const SHEET_INK = '#18181B';
const SHEET_WHITE = '#FFFFFF';

const FULL_HOLES = [6.6, 10.2, 13.8, 17.4].map((cx) => ({ cx, r: 1.25 }));
const SMALL_HOLES = [7.2, 12, 16.8].map((cx) => ({ cx, r: 1.6 }));
const FULL_ROTATION = -7;
const SMALL_ROTATION = -4;

/**
 * The tear-off mark: a pad stub with a perforated sheet peeling away from it,
 * standing in for the report the operator hands off. Stub and sheet are
 * separate shapes so either can render in a single color.
 */
export function Mark(props: {
  size: number;
  tone?: 'light' | 'dark';
  variant?: 'full' | 'small';
}): ReactElement {
  const id = useId();
  // React's useId includes colons (e.g. ":r0:"), which are valid in HTML ids
  // but not guaranteed safe in every SSR/XML consumer of an `id`/`url(#...)`
  // reference; strip everything but letters, digits, `-`, and `_`.
  const maskId = `rp-mark-holes-${id.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const sheetFill = props.tone === 'dark' ? SHEET_WHITE : SHEET_INK;
  const small = props.variant ? props.variant === 'small' : props.size <= 24;
  const holes = small ? SMALL_HOLES : FULL_HOLES;
  const rotation = small ? SMALL_ROTATION : FULL_ROTATION;

  return (
    <svg width={props.size} height={props.size} viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <mask id={maskId}>
          {/* Oversized relative to the 24x24 viewBox so the mask's visible
              area covers the sheet rect even after it rotates; a rect sized
              to the viewBox would clip the sheet's rotated corners. */}
          <rect x={-4} y={-4} width={32} height={32} fill="#fff" />
          {holes.map((hole) => (
            <circle key={hole.cx} cx={hole.cx} cy={8.2} r={hole.r} fill="#000" />
          ))}
        </mask>
      </defs>
      <rect x={3} y={2} width={18} height={3.4} rx={1.7} fill={STUB_FILL} />
      <g transform={`rotate(${rotation} 12 15)`}>
        <rect x={3.4} y={8.2} width={17.2} height={13.8} rx={2.6} fill={sheetFill} mask={`url(#${maskId})`} />
      </g>
    </svg>
  );
}

/** The wordmark lockup: the mark plus "ReportPad" as one word, "Pad" set light. */
export function Wordmark(props: { tone?: 'light' | 'dark'; size?: number }): ReactElement {
  return (
    <span className="inline-flex items-center gap-2 font-semibold text-lg rp-wordmark">
      <Mark size={props.size ?? 22} tone={props.tone} />
      <span>
        Report
        <span className="font-normal">Pad</span>
      </span>
    </span>
  );
}
